/**
 * QMI Investor Portal — design tokens.
 *
 * Single source of truth for brand colors, gradients, shadows, and
 * surface treatments. Every screen (auth, app shell, dashboards)
 * MUST consume these tokens instead of hardcoding hex values, so that
 * a future rebrand only touches this file.
 */

export const colors = {
  navy: '#06091A',
  navyLight: '#0D1730',
  navyMid: '#11183A',

  // Gold is for navy chrome and for fills under navy text: on white it is 2.21:1. Gold
  // TEXT, a gold link or the gold edge of a control on a light surface is `goldOnLight`.
  gold: '#D4A84B',
  goldLight: '#F0C455',
  goldOnLight: '#85651A',

  // Neutrals tuned to sit cleanly on white/navy chrome. Every text token meets WCAG 2.1 AA
  // (4.5:1) on the light surfaces; `styles/contrast.test.ts` measures each pair.
  ink: '#111827',
  inkSoft: '#374151',
  muted: '#6B7280',
  mutedSoft: '#666E7C',

  // The edge of a text field, a select or a checkbox: 3:1 against the surface (SC 1.4.11).
  controlBorder: '#848C99',
  // The keyboard focus ring: 3:1 against white and against the navy chrome (SC 2.4.7).
  focusRing: '#2563EB',

  // Surfaces
  canvas: '#F8F9FC',
  surface: '#FFFFFF',
  border: '#E8ECF4',
  borderSoft: '#F0F0F0',

  // Status (kept conservative for fintech)
  danger: '#B91C1C',
  dangerBg: '#FEF2F2',
  dangerBorder: '#FECACA',
  success: '#047857',
  successBg: '#ECFDF5',
  successBorder: '#BBF7D0',
  successText: '#166534',
  warning: '#B45309',
  warningBg: '#FFFBEB',

  // Form-flavored aliases — historically lived in
  // `components/forms/tokens.ts`. Kept here so the design system has a
  // single source of truth; that module is now a thin re-export.
  errorBg: '#FEF2F2',
  errorBorder: '#FECACA',
  errorText: '#B91C1C',
  infoBg: '#EFF6FF',
  infoBorder: '#BFDBFE',
  infoText: '#1D4ED8',
  disabledBg: '#E5E7EB',
  disabledText: '#9CA3AF',
  surfaceMuted: '#F8F9FC',
  textPrimary: '#111827',
  textMuted: '#6B7280',
  textLabel: '#4B5563',
  textPlaceholder: '#666E7C',
} as const;

export const gradients = {
  navy: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.navyLight} 100%)`,
  gold: `linear-gradient(135deg, ${colors.gold} 0%, ${colors.goldLight} 100%)`,
  goldText: `linear-gradient(90deg, ${colors.gold} 0%, ${colors.goldLight} 100%)`,
} as const;

export const shadows = {
  card: '0 32px 80px rgba(0,0,0,0.45)',
  goldCta: `0 6px 24px rgba(212,168,75,0.35)`,
  topbar: '0 1px 0 rgba(255,255,255,0.04), 0 8px 24px rgba(0,0,0,0.18)',
  menu: '0 12px 32px rgba(6,9,26,0.18)',
} as const;

export const radii = {
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '20px',
  pill: '9999px',
} as const;

// Convenience aliases for the few legacy call-sites that still spell
// the constants in SCREAMING_CASE. New code should import from `colors`.
export const NAVY = colors.navy;
export const NAVY_LIGHT = colors.navyLight;
export const GOLD = colors.gold;
export const GOLD_LIGHT = colors.goldLight;
